export default function ImgPilotPrivacy() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            Privacy Policy - Image Compressor &amp; Resizer
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Effective date: <time dateTime="2026-07-05">July 5, 2026</time>
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <p>
            <strong>Cosmovex</strong> built <strong>Image Compressor &amp;
            Resizer</strong> (ImgPilot) as an ad-supported application. This
            Privacy Policy explains what information ImgPilot may process,
            how that information is used, and the choices you have when
            using the app.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Information We Process
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              <strong>Photos you edit:</strong> images you pick to compress,
              resize, crop, convert, enhance, rotate, remove the background
              from, or watermark are processed entirely on your device.
              They are never uploaded to our servers.
            </li>
            <li>
              <strong>Exported files:</strong> compressed, resized, or
              converted images are written to the location you pick in the
              system save dialog, or shared directly to the app you choose.
              They are not sent anywhere else.
            </li>
            <li>
              <strong>App preferences:</strong> theme choice and Pro-upgrade
              status are stored locally on your device.
            </li>
            <li>
              <strong>Usage and ad data:</strong> if ads are enabled, AdMob
              or related Google Mobile Ads services may collect device and
              advertising data as described in Google&apos;s own privacy
              documentation.
            </li>
            <li>
              <strong>Analytics and diagnostics:</strong> Firebase Analytics
              and Firebase Crashlytics may collect app stability, usage
              patterns, and crash reports to improve reliability and user
              experience.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            How We Use Information
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              To compress, resize, crop, convert, enhance, rotate, and
              watermark photos on your device.
            </li>
            <li>
              To remove the background from a photo on your device, offline.
            </li>
            <li>
              To strip hidden metadata (EXIF data, such as location and
              device info) from a photo when you choose to.
            </li>
            <li>
              To export or share the resulting image to a location or app
              you choose.
            </li>
            <li>
              To remember your preferences and Pro-upgrade status.
            </li>
            <li>
              To improve app performance, reliability, and product quality.
            </li>
            <li>
              To display ads in supported versions of the app, where
              applicable.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Local Storage
          </h2>
          <p>
            ImgPilot is designed to keep your photos on your device. Source
            images, edited results, and preferences are stored locally and
            are not uploaded to any server. Data remains on your device
            until you remove it, clear the app&apos;s storage, or uninstall
            the app.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Network Access
          </h2>
          <p>
            Compressing, resizing, cropping, converting, enhancing, and
            removing the background from a photo does not require network
            access — every one of these tools runs entirely on your device.
            The app makes network requests only for ads, Firebase analytics
            and crash reporting, and the rotating cross-promotion banner
            shown on the home screen. No photo, image byte, or file content
            is ever transmitted.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Advertising</h2>
          <p>
            ImgPilot may use <strong>Google AdMob</strong> to show banner
            and interstitial ad formats. AdMob may collect and process
            identifiers, device information, approximate location, and ad
            interaction data to deliver and measure ads. You can remove ads
            via a one-time Pro upgrade.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            In-App Purchases
          </h2>
          <p>
            ImgPilot offers an optional one-time Pro upgrade that removes
            advertisements and unlocks batch processing, on-device
            background removal, and watermarking. Purchases are processed
            through Google Play and are subject to Google&apos;s terms and
            privacy policies.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Third-Party Services
          </h2>
          <p>ImgPilot may rely on third-party services such as:</p>
          <ul className="list-disc space-y-2 pl-6">
            <li>Google Play Services</li>
            <li>Google AdMob</li>
            <li>Firebase Analytics</li>
            <li>Firebase Crashlytics</li>
            <li>Firebase Remote Config</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">Data Sharing</h2>
          <p>
            We do not sell your photos or personal data. Data may be shared
            only with service providers required to operate app features,
            such as advertising, analytics, app distribution, or
            diagnostics infrastructure.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Data Retention
          </h2>
          <p>
            Local app data remains on your device until you remove it,
            clear the app&apos;s storage, or uninstall the app. Data
            collected by third-party services such as AdMob and Firebase is
            retained according to those providers&apos; own policies.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Children&apos;s Privacy
          </h2>
          <p>
            ImgPilot is not intended for children under 13, and we do not
            knowingly collect personal information from children through
            the app.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Security</h2>
          <p>
            We take reasonable steps to protect data processed by the app,
            but no method of storage or transmission is completely secure.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Your Choices
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>You can delete individual photos from inside the app.</li>
            <li>
              You can uninstall the app to remove all locally stored data
              from your device.
            </li>
            <li>
              You can purchase the Pro upgrade to disable all advertising.
            </li>
            <li>
              You can manage ad personalization and advertising controls
              through your device and Google settings.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Changes to This Policy
          </h2>
          <p>
            We may update this Privacy Policy from time to time. When we
            do, we will post the updated version on this page and revise
            the effective date.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Contact Us</h2>
          <p>
            For privacy-related questions about Image Compressor &amp;
            Resizer, contact{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            .
          </p>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-sm text-[#8A93A2]">
          &copy; 2026 Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
